"use client";

import { cn } from "@frontend/shared/lib/utils";
import * as React from "react";
import { formatPhoneNumber } from "../lib/phone-utils";

interface KioskInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
}

const KioskInput = React.forwardRef<HTMLInputElement, KioskInputProps>(
  ({ className, error, ...props }, ref) => {
    return (
      <input
        ref={ref}
        className={cn(
          "flex h-[44px] md:h-[50px] w-full rounded-full",
          "bg-white/80 px-4 py-2 text-base md:px-6 md:py-2.5 md:text-lg",
          "border border-gray-200",
          "placeholder:text-gray-500",
          "hover:bg-white hover:border-gray-300",
          "focus:outline-none focus:border-black focus:ring-0 focus:ring-offset-0",
          "disabled:cursor-not-allowed disabled:opacity-50",
          error && "border-red-500 focus:ring-red-500",
          className,
        )}
        {...props}
      />
    );
  },
);
KioskInput.displayName = "KioskInput";

const KioskNameInput = React.forwardRef<HTMLInputElement, KioskInputProps>(
  (props, ref) => {
    return (
      <KioskInput
        ref={ref}
        {...props}
        autoCorrect="off"
        spellCheck={false}
        autoCapitalize="words"
      />
    );
  },
);
KioskNameInput.displayName = "KioskNameInput";

const KioskPhoneInput = React.forwardRef<HTMLInputElement, KioskInputProps>(
  ({ className, error, onChange, ...props }, ref) => {
    return (
      <input
        onChange={(e) => {
          e.preventDefault();
          const newChar = e.target.value.slice(-1);
          if (/[a-zA-Z]/.test(newChar)) {
            return;
          }
          const raw = e.target.value;
          const isInternational = raw.startsWith("+");
          let formatted: string;
          if (isInternational) {
            const digits = raw.replace(/\D/g, "");
            if (digits.startsWith("1") && digits.length > 1) {
              // +1 prefix with additional digits — format as US
              const usDigits = digits.slice(1, 11);
              formatted = formatPhoneNumber(usDigits);
            } else {
              // Other international — pass through
              formatted = `+${digits}`;
            }
          } else {
            const digits = raw.replace(/\D/g, "").slice(0, 10);
            formatted = formatPhoneNumber(digits);
          }
          onChange?.({
            target: {
              ...e.target,
              value: formatted,
            },
          } as React.ChangeEvent<HTMLInputElement>);
        }}
        placeholder="(XXX) XXX-XXXX"
        type="tel"
        ref={ref}
        className={cn(
          "flex h-[44px] md:h-[50px] w-full rounded-full",
          "bg-white/80 px-4 py-2 text-sm md:px-6 md:py-2.5 md:text-base",
          "border border-gray-200",
          "placeholder:text-gray-500",
          "hover:bg-white hover:border-gray-300",
          "focus:outline-none focus:border-black focus:ring-0 focus:ring-offset-0",
          "disabled:cursor-not-allowed disabled:opacity-50",
          error && "border-red-500 focus:ring-red-500",
          className,
        )}
        {...props}
      />
    );
  },
);
KioskPhoneInput.displayName = "KioskPhoneInput";

interface KioskInputWithIconProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  icon?: React.ReactNode;
}

const KioskInputWithIcon = React.forwardRef<
  HTMLInputElement,
  KioskInputWithIconProps
>(
  (
    { className, error, icon, placeholder, value, defaultValue, ...props },
    ref,
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const [internalValue, setInternalValue] = React.useState(
      defaultValue || "",
    );
    const [isFocused, setIsFocused] = React.useState(false);
    // Combine refs
    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    // Forward focus and click events to input
    const handleWrapperClick = () => {
      inputRef.current?.focus();
    };

    const handleWrapperKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInternalValue(e.target.value);
      props.onChange?.(e);
    };

    // Determine if input has a value (for controlled or uncontrolled)
    const currentValue = value !== undefined ? value : internalValue;
    const hasValue =
      currentValue !== undefined &&
      currentValue !== null &&
      currentValue !== "";

    return (
      <div
        ref={wrapperRef}
        className={cn(
          "relative flex h-[44px] md:h-[50px] w-full rounded-full",
          "bg-white/80 px-4 py-2 text-sm md:px-6 md:py-2.5 md:text-base",
          "border border-gray-200",
          "hover:bg-white hover:border-gray-300",
          "focus-within:outline-none focus-within:border-black focus-within:ring-0 focus-within:ring-offset-0",
          "disabled:cursor-not-allowed disabled:opacity-50",
          error && "border-red-500 focus-within:ring-red-500",
          className,
        )}
        onClick={handleWrapperClick}
        onKeyDown={handleWrapperKeyDown}
      >
        {icon && !hasValue && (
          <div className="absolute left-6 top-1/2 -translate-y-1/2 pointer-events-none flex items-center gap-2">
            <span className="text-gray-500">{icon}</span>
            {placeholder && (
              <span className="text-gray-500 text-sm md:text-base">
                {placeholder}
              </span>
            )}
          </div>
        )}
        <input
          ref={inputRef}
          onFocus={(e) => {
            setIsFocused(true);
          }}
          onBlur={(e) => {
            setIsFocused(false);
          }}
          className={cn(
            "w-full bg-transparent outline-none",
            "text-sm md:text-base",
            "placeholder:text-transparent",
            "disabled:cursor-not-allowed",
          )}
          placeholder={placeholder}
          value={value}
          defaultValue={defaultValue}
          onChange={handleInputChange}
          {...props}
        />
      </div>
    );
  },
);
KioskInputWithIcon.displayName = "KioskInputWithIcon";

export { KioskInput, KioskNameInput, KioskPhoneInput, KioskInputWithIcon };
